import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface LanguageContextProps {
  language: "en" | "hu";
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Set the initial language based on the URL path
  const initialLanguage = location.pathname.startsWith("/hu") ? "hu" : "en";
  const [language, setLanguage] = useState<"en" | "hu">(initialLanguage);

  const toggleLanguage = () => {
    const newLang = language === "en" ? "hu" : "en";
    setLanguage(newLang);

    // Update the URL's language prefix
    const currentPath = location.pathname.replace(/^\/(en|hu)/, "");
    navigate(`/${newLang}${currentPath}`);
  };

  useEffect(() => {
    // Sync language state with URL on path changes
    const urlLang = location.pathname.startsWith("/hu") ? "hu" : "en";
    if (urlLang !== language) {
      setLanguage(urlLang);
    }
  }, [location.pathname]);

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
